import React, { useState } from "react";
import { CaretLeft, Plus } from "@phosphor-icons/react";
import CatIcon from "./CatIcon";
import EntryCard from "./EntryCard";
import AddEntryForm from "./AddEntryForm";
import PhotoIdForm, { PhotoIdMethodPicker } from "./PhotoIdForm";
import InsuranceForm, { InsuranceMethodPicker } from "./InsuranceForm";
import FlightForm, { FlightMethodPicker } from "./FlightForm";
import { MAX_FAMILY, VAULT_SURFACE_STYLE } from "./vaultSchema";

/**
 * Detail screen for a single Vault category. Lists entries, shows the
 * add-flow appropriate to the category (3-method picker for photo_id,
 * plain form for everything else), and enforces the family cap.
 */
export default function CategoryView({
  cat,
  entries,
  familyMembers,
  onBack,
  onAdd,
  onRemove,
  adding,
  onSave,
  onCancelAdd,
}) {
  const [addMode, setAddMode] = useState(null); // null | "manual" | "scan" | "photo"

  function exitForm() {
    setAddMode(null);
    onCancelAdd();
  }

  const showPhotoIdPicker = cat.id === "photo_id" && adding && !addMode;
  const showInsurancePicker = cat.id === "insurance" && adding && !addMode;
  const showFlightPicker = cat.id === "flight" && adding && !addMode;
  const usesMethodPicker =
    cat.id === "photo_id" || cat.id === "insurance" || cat.id === "flight";
  const inForm = adding && (!usesMethodPicker || !!addMode);
  const familyAtMax = cat.id === "family" && entries.length >= MAX_FAMILY;

  // When the category has 0-2 entries AND we're not in an active add
  // flow, vertically center the content on the steel background. The
  // cards used to stack flush against the header which made the steel
  // wall background feel like a giant empty wall below them. Centering
  // anchors the content and keeps the steel aesthetic consistent.
  const isLightResting = !adding && entries.length <= 2;

  return (
    <div
      className="px-5 pt-4 pb-28 min-h-screen bg-bone flex flex-col"
      style={VAULT_SURFACE_STYLE}
      data-testid={`category-${cat.id}`}
    >
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={onBack}
          data-testid="vault-back"
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <h2 className="font-jakarta font-extrabold text-navy text-xl flex items-center gap-2">
          <CatIcon cat={cat} size={22} />
          {cat.label}
        </h2>
        <div className="w-10 h-10" />
      </div>

      {/* Light-content state (0–2 entries): vertically center the card(s)
          + Add button on the steel background. Wrapping them in a flex-1
          column with `justify-center` keeps the content anchored just
          above the device midline. */}
      {isLightResting ? (
        <div className="flex-1 flex flex-col justify-center -mt-12">
          {entries.length === 0 && (
            <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-8 text-center">
              <img
                src={cat.buttonSrc}
                alt=""
                aria-hidden
                draggable={false}
                className="w-20 h-20 mx-auto mb-3 object-contain"
              />
              <div className="font-jakarta font-extrabold text-navy text-lg mb-1">
                No {cat.label.toLowerCase()} saved yet
              </div>
              <div className="text-navy-soft text-sm font-jakarta font-medium">
                {cat.id === "family"
                  ? `Add up to ${MAX_FAMILY} family members so they show up in your ID picker.`
                  : `Tap the button below to add your first ${cat.label.toLowerCase()}.`}
              </div>
            </div>
          )}

          {entries.length > 0 && (
            <div className="space-y-3">
              {entries.map((e) => (
                <EntryCard
                  key={e.id}
                  cat={cat}
                  entry={e}
                  familyMembers={familyMembers}
                  onRemove={() => onRemove(e.id)}
                />
              ))}
            </div>
          )}

          {!familyAtMax && (
            <button
              type="button"
              onClick={onAdd}
              data-testid="vault-add-btn"
              className="w-full mt-5 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft flex items-center justify-center gap-2"
            >
              <Plus size={18} weight="bold" />
              {cat.id === "family" ? "Add family member" : `Add ${cat.label.toLowerCase()}`}
            </button>
          )}
          {familyAtMax && (
            <div className="mt-5 bg-white border border-navy/10 rounded-2xl px-4 py-3 text-center text-navy-soft text-sm font-jakarta font-medium">
              You've reached the {MAX_FAMILY}-family-member limit for this beta. Remove
              one to add another.
            </div>
          )}
        </div>
      ) : null}

      {entries.length > 2 && !adding && (
        <div className="space-y-3">
          {entries.map((e) => (
            <EntryCard
              key={e.id}
              cat={cat}
              entry={e}
              familyMembers={familyMembers}
              onRemove={() => onRemove(e.id)}
            />
          ))}
        </div>
      )}

      {showPhotoIdPicker && (
        <PhotoIdMethodPicker onPick={setAddMode} onCancel={onCancelAdd} />
      )}

      {showInsurancePicker && (
        <InsuranceMethodPicker onPick={setAddMode} onCancel={onCancelAdd} />
      )}

      {showFlightPicker && (
        <FlightMethodPicker onPick={setAddMode} onCancel={onCancelAdd} />
      )}

      {inForm && cat.id === "photo_id" && (
        <PhotoIdForm
          mode={addMode}
          familyMembers={familyMembers}
          onSave={(entry) => {
            onSave(entry);
            setAddMode(null);
          }}
          onCancel={exitForm}
        />
      )}

      {inForm && cat.id === "insurance" && (
        <InsuranceForm
          mode={addMode}
          familyMembers={familyMembers}
          onSave={(entry) => {
            onSave(entry);
            setAddMode(null);
          }}
          onCancel={exitForm}
        />
      )}

      {inForm && cat.id === "flight" && (
        <FlightForm
          mode={addMode}
          onSave={(entry) => {
            onSave(entry);
            setAddMode(null);
          }}
          onCancel={exitForm}
        />
      )}

      {inForm && !usesMethodPicker && (
        <AddEntryForm
          cat={cat}
          familyMembers={familyMembers}
          onSave={onSave}
          onCancel={onCancelAdd}
        />
      )}

      {!adding && !familyAtMax && !isLightResting && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="vault-add-btn"
          className="w-full mt-5 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft flex items-center justify-center gap-2"
        >
          <Plus size={18} weight="bold" />
          {cat.id === "family" ? "Add family member" : `Add ${cat.label.toLowerCase()}`}
        </button>
      )}

      {familyAtMax && !isLightResting && (
        <div className="mt-5 bg-white border border-navy/10 rounded-2xl px-4 py-3 text-center text-navy-soft text-sm font-jakarta font-medium">
          You've reached the {MAX_FAMILY}-family-member limit for this beta. Remove
          one to add another.
        </div>
      )}
    </div>
  );
}
